import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, EMPTY, finalize, Observable, throwError } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { TokenService } from '@services/helpers/token.service';
import { isPublicReportLocation } from '@services/helpers/public-report-route';
import { BASE_URL } from '../models/global-constant.model';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private isRefreshInFlight = false;

  constructor(
    private readonly tokenService: TokenService,
    private readonly http: HttpClient,
    private _authService: AuthService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Match the auth login endpoint specifically — a broad `includes('login')` also matches
    // unrelated paths like `/login-reports/*`, stripping their bearer token and forcing a 401 logout.
    const isLoginRequest = request.url.includes('/auth/login');
    const isRefreshRequest = request.url.includes('refresh');
    /** Must be called without Authorization (even if a stale token exists in storage). */
    const isPublicReportSession = request.url.includes('data-report/public/session');
    const onPublicReportPath = this.isPublicReportViewPath();
    const onAnonymousPublicPath = this.isAnonymousPublicPath();
    /**
     * On an anonymous public page a stale/expired token must be ignored: treat the visitor as
     * anonymous so the page loads instead of attempting a refresh (which 401s) and redirecting
     * to login. A still-valid token keeps working normally.
     *
     * Every token read below goes through `safe()`: this method runs OUTSIDE the observable pipe,
     * so anything that throws here escapes synchronously and the request neither fires nor errors,
     * leaving the caller's subscribe silent and the page spinning forever.
     */
    const isTokenExpired = this.safe(() => this.tokenService.isTokenExpired(), true);
    const isExpiredTokenOnAnonymousPage = onAnonymousPublicPath && isTokenExpired;
    const token = this.safe(() => this.tokenService.getToken(), null);
    /**
     * Anonymous public report viewer (no valid real login token on the `/report-view` path): the
     * report data calls are authorized by the scoped viewer token, which is stored under its own
     * key — separate from the shared login `token` — so it never corrupts a real session.
     */
    const viewerToken = onPublicReportPath && isTokenExpired ? this.safe(() => this.tokenService.getPublicReportToken(), null) : null;

    if (!isLoginRequest && !isPublicReportSession && !isExpiredTokenOnAnonymousPage && token) {
      // Never fire the refresh call from an anonymous public page: it 401s for a visitor with no
      // account and the error branch of `refreshToken()` logs them out.
      if (!onAnonymousPublicPath && isTokenExpired && !isRefreshRequest) {
        this.refreshToken();
      }
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    } else if (!isLoginRequest && !isPublicReportSession && viewerToken) {
      // No refresh attempt for the viewer token: it is short-lived and the visitor simply re-enters
      // the PIN when it expires — a refresh here would 401 and (wrongly) trigger a logout.
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${viewerToken}`,
        },
      });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const wrongPinOrSlug = error.url?.includes('data-report/public/session');
        /**
         * Anonymous public pages (PIN-gated report view, token-gated External EOI form). Their URLs
         * are sent to people who have no Remmi account, so a 401 from ANY backend call here must
         * NEVER log out or redirect to login — the page has to stay open.
         *
         * This is unconditional on those paths. Scoping it to "no valid login token" was the hole:
         * an agent whose Remmi session had a token that still LOOKED valid client-side (clock skew,
         * or a token the backend had already revoked) fell through to `logOutWithoutToken()`, which
         * wipes localStorage and navigates to `/login` — exactly the reported "it redirects the
         * customer to the login page".
         */
        /**
         * `PIN_REQUIRED` (see docs/backend/reports/data-report-access-errors.md) means "this viewer needs a
         * PIN-scoped token for this report", not "the session is invalid" — it now also covers a
         * logged-in staff member opening a report from an office they have no access to, on the
         * INTERNAL report routes (`/report/edit-report/:id`, `/report/view-report/:id`) where
         * `onAnonymousPublicPath` is false. Without this check that case would wrongly log out a real,
         * valid staff session instead of routing them to the report's PIN entry.
         */
        const pinRequired = (error.error as any)?.code === 'PIN_REQUIRED';
        if (error.status === 401 && !wrongPinOrSlug && !onAnonymousPublicPath && !pinRequired) {
          this._authService.logOutWithoutToken();
          // Token expired → we've already redirected to /login. Swallow the error so the
          // in-flight request that triggered it doesn't surface a "Token is not valid" toast.
          return EMPTY;
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Pages a visitor is meant to reach without an account. On these, a stale token is ignored and
   * a 401 must never log the visitor out or redirect them to /login.
   */
  private isAnonymousPublicPath(): boolean {
    return this.isPublicReportViewPath() || this.isPublicExternalEoiPath();
  }

  private isPublicReportViewPath(): boolean {
    return isPublicReportLocation();
  }

  /** Runs a token read that must never break the request pipeline. */
  private safe<T>(read: () => T, fallback: T): T {
    try {
      const value = read();
      return value === undefined ? fallback : value;
    } catch {
      return fallback;
    }
  }

  /**
   * The buyer-facing External EOI form, in either of its two public shapes:
   *   `/external-eoi/<token>`                       — original token link
   *   `/<project>/eoi/<lot>/<client>`               — readable link
   *
   * Matched by exact shape rather than a substring so the authenticated agent-side config tab
   * (`/projects/edit_/<id>/project-setup/external-eoi`) keeps normal 401 handling. Missing the
   * readable shape here would log the buyer out to /login instead of showing the form.
   */
  private isPublicExternalEoiPath(): boolean {
    if (typeof window === 'undefined') return false;
    const p = window.location?.pathname ?? '';
    return /^\/external-eoi\/[^/]+\/?$/.test(p) || /^\/[^/]+\/eoi\/[^/]+\/[^/]+\/?$/.test(p);
  }

  private refreshToken(): void {
    // Every request made while the token is expired lands here; one refresh serves them all.
    if (this.isRefreshInFlight) {
      return;
    }
    this.isRefreshInFlight = true;
    this.http
      .post(`${BASE_URL}/auth/refresh`, '')
      .pipe(finalize(() => (this.isRefreshInFlight = false)))
      .subscribe({
        next: response => {
          this.tokenService.handleTokenResponse(response);
        },
        error: error => {
          if (error.status === 401 && !this.isAnonymousPublicPath()) {
            this._authService.logOutWithoutToken();
          }
        },
      });
  }
}
